import * as React from 'react';
import {
  Box,
  Divider,
  Paper,
  Grid,
  Chip,
  Stack,
  Typography,
  IconButton,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  LinearProgress,
  Tooltip,
  TextField,
} from '@mui/material';
import { CloseOutlined } from '@mui/icons-material';
import type { ChipProps } from '@mui/material';
import {
  OrderEsimResponse,
  OrderResponse,
  GetUsagesLineStatus,
  GetUsagesKycDocumentType,
  GetUsagesKycStatus,
  GetUsagesProfileStatus,
  GetUsagesPackageStatus,
  GetUsagesUsageType,
} from '@apis/order';
import { BarLoader } from 'react-spinners';
import { useOrderEsimUsage } from '../../queries/orders/useOrderEsimUsage';
import { zonedTimeToUtc, formatInTimeZone } from 'date-fns-tz';

type OrderEsimUsageContentProps = {
  onClose: () => void;
  order: OrderResponse;
  esim: OrderEsimResponse | null;
  visible: boolean;
};

// History item shape as returned from the API
type UsageHistoryItem = {
  date?: string;
  usedMb?: number | string;
};

// Detect browser time zone once and reuse
const BROWSER_TIME_ZONE =
  Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

/**
 * Format a KST (Asia/Seoul) datetime string into the browser's local time zone.
 * If parsing fails, returns the original string.
 */
const formatKstToLocal = (
  value?: string,
  pattern = 'yyyy-MM-dd HH:mm:ss'
): string => {
  if (!value) return '-';

  try {
    const utcDate = zonedTimeToUtc(value, 'Asia/Seoul');
    return formatInTimeZone(utcDate, BROWSER_TIME_ZONE, pattern);
  } catch {
    return value;
  }
};

/**
 * Safely convert null/undefined to empty string.
 * Used for controlled TextField values.
 */
const safe = (v: unknown): string => (v == null ? '' : String(v));

/**
 * Show "-" in tables when value is null, undefined, or empty string.
 */
const safeDash = (v: unknown): string =>
  v == null || v === '' ? '-' : String(v);

/**
 * Merge history rows that share the same date,
 * summing their usedMb values and sorting chronologically.
 */
const normalizeHistory = (history: UsageHistoryItem[]) => {
  const map = new Map<string, number>();

  history.forEach((item) => {
    if (!item.date) return;
    const date = item.date;
    const used = Number(item.usedMb) || 0;

    map.set(date, (map.get(date) || 0) + used);
  });

  return Array.from(map.entries())
    .map(([date, totalUsed]) => ({ date, usedMb: totalUsed }))
    .sort((a, b) => a.date.localeCompare(b.date));
};

/**
 * Line status → Chip renderer.
 * Hides UNKNOWN and unsupported values.
 */
const renderLineStatusChip = (status?: GetUsagesLineStatus | string) => {
  if (!status || status === GetUsagesLineStatus.UNKNOWN || status === 'UNKNOWN') {
    return null;
  }

  const value = String(status).toUpperCase();

  const map: Record<
    string,
    { label: string; color: ChipProps['color']; variant: ChipProps['variant'] }
  > = {
    [GetUsagesLineStatus.ACTIVATED]: {
      label: 'Activated',
      color: 'success',
      variant: 'filled',
    },
    [GetUsagesLineStatus.SUSPENDED]: {
      label: 'Suspended',
      color: 'warning',
      variant: 'filled',
    },
    [GetUsagesLineStatus.TERMINATED]: {
      label: 'Terminated',
      color: 'error',
      variant: 'filled',
    },
  };

  const config = map[value];
  if (!config) return null;

  return (
    <Chip
      label={config.label}
      color={config.color}
      variant={config.variant}
      size="small"
      sx={{ fontWeight: 500 }}
    />
  );
};

/**
 * KYC status → Chip renderer.
 * UNKNOWN is rendered as "-" for consistency with table cells.
 */
const renderKycStatusChip = (status?: GetUsagesKycStatus | string) => {
  if (
    !status ||
    status === GetUsagesKycStatus.UNKNOWN ||
    status === 'UNKNOWN'
  ) {
    return '-';
  }

  const value = String(status);

  const map: Record<
    string,
    { label: string; color: ChipProps['color']; variant: ChipProps['variant'] }
  > = {
    [GetUsagesKycStatus.CERTIFIED]: {
      label: 'Certified',
      color: 'success',
      variant: 'filled',
    },
    [GetUsagesKycStatus.VERIFYING]: {
      label: 'Verifying',
      color: 'info',
      variant: 'outlined',
    },
    [GetUsagesKycStatus.TO_BE_CERTIFIED]: {
      label: 'To be certified',
      color: 'warning',
      variant: 'outlined',
    },
    [GetUsagesKycStatus.AUTHENTICATION_FAILED]: {
      label: 'Authentication failed',
      color: 'error',
      variant: 'filled',
    },
    [GetUsagesKycStatus.EXPIRED]: {
      label: 'Expired',
      color: 'error',
      variant: 'outlined',
    },
  };

  const config = map[value];
  if (!config) return value;

  return (
    <Chip
      label={config.label}
      color={config.color}
      variant={config.variant}
      size="small"
      sx={{ fontWeight: 500 }}
    />
  );
};

/**
 * KYC document type → human-readable label.
 */
const formatKycDocumentType = (
  docType?: GetUsagesKycDocumentType | string
): string => {
  if (
    !docType ||
    docType === GetUsagesKycDocumentType.UNKNOWN ||
    docType === 'UNKNOWN'
  ) {
    return '-';
  }

  const map: Record<string, string> = {
    [GetUsagesKycDocumentType.PASSPORT]: 'Passport',
    [GetUsagesKycDocumentType.HK_MACAU_PASS]: 'HK / Macau Pass',
    [GetUsagesKycDocumentType.HK_ID_CARD]: 'Hong Kong ID Card',
    [GetUsagesKycDocumentType.MACAU_ID_CARD]: 'Macau ID Card',
  };

  return map[docType] ?? docType.replace(/_/g, ' ').toLowerCase();
};

/**
 * eSIM profile status → Chip renderer.
 * Groups pre-issue, installed, enabled/disabled, and deleted states.
 */
const renderEsimProfileStatusChip = (
  status?: GetUsagesProfileStatus | string
) => {
  if (
    !status ||
    status === GetUsagesProfileStatus.UNKNOWN ||
    status === 'UNKNOWN'
  ) {
    return null;
  }

  const value = String(status).toUpperCase();

  const map: Record<
    string,
    { label: string; color: ChipProps['color']; variant: ChipProps['variant'] }
  > = {
    // Pre-issue states
    [GetUsagesProfileStatus.AVAILABLE]: {
      label: 'Available',
      color: 'default',
      variant: 'outlined',
    },
    [GetUsagesProfileStatus.ALLOCATED]: {
      label: 'Allocated',
      color: 'default',
      variant: 'outlined',
    },

    // Issued, ready to download
    [GetUsagesProfileStatus.RELEASED]: {
      label: 'Ready to download',
      color: 'info',
      variant: 'filled',
    },

    // Downloaded / installed on device
    [GetUsagesProfileStatus.DOWNLOADED]: {
      label: 'Downloaded',
      color: 'info',
      variant: 'outlined',
    },
    [GetUsagesProfileStatus.INSTALLED]: {
      label: 'Installed',
      color: 'success',
      variant: 'outlined',
    },

    // ON / OFF states
    [GetUsagesProfileStatus.ENABLED]: {
      label: 'Enabled',
      color: 'success',
      variant: 'filled', // ON
    },
    [GetUsagesProfileStatus.DISABLED]: {
      label: 'Disabled',
      color: 'success',
      variant: 'outlined', // OFF
    },

    // Deleted profile
    [GetUsagesProfileStatus.DELETED]: {
      label: 'Deleted',
      color: 'error',
      variant: 'filled',
    },
  };

  const config = map[value];
  if (!config) return null;

  return (
    <Chip
      label={config.label}
      color={config.color}
      variant={config.variant}
      size="small"
      sx={{ fontWeight: 500 }}
    />
  );
};

/**
 * Package status → Chip renderer.
 * Reflects provisioning, active, consumed, and terminal states.
 */
const renderPackageStatusChip = (
  status?: GetUsagesPackageStatus | string
) => {
  if (!status || status === GetUsagesPackageStatus.UNKNOWN) return null;

  const value = String(status).toUpperCase();

  const map: Record<
    string,
    { label: string; color: ChipProps['color']; variant: ChipProps['variant'] }
  > = {
    // No package attached (blank SIM)
    [GetUsagesPackageStatus.NO_PACKAGE]: {
      label: 'No package',
      color: 'default',
      variant: 'outlined',
    },

    // Provisioning / updating package
    [GetUsagesPackageStatus.PROVISIONING]: {
      label: 'Provisioning',
      color: 'default',
      variant: 'filled',
    },
    [GetUsagesPackageStatus.UPDATING]: {
      label: 'Updating',
      color: 'default',
      variant: 'filled',
    },

    // Not started yet
    [GetUsagesPackageStatus.UNUSED]: {
      label: 'Not activated',
      color: 'info',
      variant: 'filled',
    },

    // Currently in use
    [GetUsagesPackageStatus.INUSE]: {
      label: 'In use',
      color: 'success',
      variant: 'filled',
    },

    // Fully consumed
    [GetUsagesPackageStatus.USED]: {
      label: 'Used',
      color: 'error',
      variant: 'filled',
    },

    // Expired or refunded
    [GetUsagesPackageStatus.EXPIRED]: {
      label: 'Expired',
      color: 'warning',
      variant: 'filled',
    },
    [GetUsagesPackageStatus.REFUNDED]: {
      label: 'Refunded',
      color: 'warning',
      variant: 'filled',
    },
  };

  const config = map[value];
  if (!config) return null;

  return (
    <Chip
      label={config.label}
      color={config.color}
      variant={config.variant}
      size="small"
      sx={{ fontWeight: 500 }}
    />
  );
};

/**
 * Format numeric value as "<number> MB" with exactly two decimal places.
 * Non-numeric values fall back to a plain string.
 */
const formatMb = (v: unknown): string => {
  if (v == null || v === '') return '-';

  const num = Number(v);
  if (Number.isNaN(num)) return String(v);

  const fixed = num.toFixed(2);
  return `${fixed} MB`;
};

/**
 * Usage type → Chip renderer for DAILY_RESET / TOTAL_QUOTA / UNLIMITED.
 */
const renderUsageTypeChip = (type?: GetUsagesUsageType | string) => {
  if (!type) return null;

  const value = String(type).toUpperCase();

  const map: Record<
    string,
    { label: string; color: ChipProps['color']; variant: ChipProps['variant'] }
  > = {
    [GetUsagesUsageType.DAILY_RESET]: {
      label: 'Daily reset',
      color: 'info',
      variant: 'outlined',
    },
    [GetUsagesUsageType.TOTAL_QUOTA]: {
      label: 'Total quota',
      color: 'primary',
      variant: 'outlined',
    },
    [GetUsagesUsageType.UNLIMITED]: {
      label: 'Unlimited',
      color: 'success',
      variant: 'filled',
    },
  };

  const config = map[value];
  if (!config) return null;

  return (
    <Chip
      label={config.label}
      color={config.color}
      variant={config.variant}
      size="small"
      sx={{ fontWeight: 500 }}
    />
  );
};

/**
 * Reusable read-only TextField used across cards for displaying info values.
 */
type InfoFieldProps = {
  label: string;
  value: unknown;
};

const InfoField: React.FC<InfoFieldProps> = ({ label, value }) => (
  <TextField
    label={label}
    value={safe(value)}
    fullWidth
    size="small"
    disabled
    // Always shrink label so empty values still show a proper label
    InputLabelProps={{ shrink: true }}
  />
);

/**
 * Main content component for the "Show Usage" modal.
 * Displays line, KYC, eSIM profile, package, and usage information
 * for a single eSIM within an order.
 */
export const OrderEsimUsageContent = ({
  onClose,
  order,
  esim,
  visible,
}: OrderEsimUsageContentProps) => {
  const { data, isLoading } = useOrderEsimUsage(
    order.orderId,
    esim?.esimId ?? 0,
    visible
  );

  const usage = data?.data?.[0] ?? {};

  const kycStatuses = usage?.line?.kycStatus ?? [];

  // Usage type & normalized history (merged by date)
  const usageType = usage?.usage?.type as GetUsagesUsageType | undefined;
  const rawHistory: UsageHistoryItem[] = usage?.usage?.history ?? [];
  const usageHistory = normalizeHistory(rawHistory);

  // Total quota from API (0 for unlimited plans)
  const totalMb = Number(usage?.usage?.totalMb) || 0;

  // Today's aggregated usage from API
  const todayUsedMb = Number(usage?.usage?.usedMb) || 0;

  // Sum of historical usage (assumes history does not include today's usage)
  const historyUsedTotalMb = usageHistory.reduce((sum, row) => {
    const v = Number(row.usedMb);
    return sum + (Number.isNaN(v) ? 0 : v);
  }, 0);

  // For unlimited plans, show total usage (history + today).
  // For limited plans, rely only on today's aggregated usage from API.
  const usedMb =
    usageType === GetUsagesUsageType.UNLIMITED
      ? historyUsedTotalMb + todayUsedMb
      : todayUsedMb;

  const usagePercent =
    totalMb > 0 ? Math.min(100, Math.round((usedMb / totalMb) * 100)) : 0;

  const lineStatus = usage?.line?.lineStatus as GetUsagesLineStatus | undefined;
  const profileStatus = usage?.esim?.profileStatus as
    | GetUsagesProfileStatus
    | undefined;
  const packageStatus = usage?.package
    ?.packageStatus as GetUsagesPackageStatus | undefined;

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      {/* Top loading bar while fetching usage data */}
      <BarLoader loading={isLoading} width="100%" height="8px" />

      {/* Header */}
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box>
          <Typography variant="h6">SIM Information</Typography>
          <Typography variant="caption" color="text.secondary">
            Timestamps are converted from KST (UTC+9) to your local time zone (
            {BROWSER_TIME_ZONE}). Usage history dates are reported by carriers
            as-is.
          </Typography>
        </Box>

        <IconButton size="small" onClick={onClose}>
          <CloseOutlined />
        </IconButton>
      </Box>

      <Divider />

      {/* LINE INFORMATION */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="subtitle1">Line Information</Typography>
          {renderLineStatusChip(lineStatus)}
        </Stack>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <InfoField label="ICCID" value={usage?.line?.iccid} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <InfoField label="MSISDN" value={usage?.line?.msisdn} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <InfoField label="IMEI" value={usage?.line?.imei} />
          </Grid>
        </Grid>
      </Paper>

      {/* KYC INFORMATION */}
      {kycStatuses.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle1" mb={2}>
            KYC Information
          </Typography>

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Country / Region</TableCell>
                <TableCell>ID Type</TableCell>
                <TableCell>Expiry Date</TableCell>
                <TableCell>KYC Status</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {kycStatuses.map((status: any, index: number) => (
                <TableRow
                  key={`${status?.mcc ?? 'mcc'}-${
                    status?.docExpDate ?? 'exp'
                  }-${index}`}
                >
                  <TableCell>{safeDash(status?.name)}</TableCell>
                  <TableCell>{formatKycDocumentType(status?.docType)}</TableCell>
                  <TableCell>{safeDash(status?.docExpDate)}</TableCell>
                  <TableCell>{renderKycStatusChip(status?.status)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* eSIM PROFILE */}
      {profileStatus && profileStatus !== GetUsagesProfileStatus.UNKNOWN && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="subtitle1">eSIM Profile</Typography>
            {renderEsimProfileStatusChip(profileStatus)}
          </Stack>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <InfoField
                label="Installed on"
                value={formatKstToLocal(usage?.esim?.installedAt)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <InfoField label="EID (eSIM ID)" value={usage?.esim?.eid} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <InfoField
                label="Device model"
                value={usage?.esim?.deviceModel}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <InfoField
                label="Last updated"
                value={formatKstToLocal(usage?.esim?.lastChangedAt)}
              />
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* PACKAGE INFORMATION */}
      {packageStatus &&
        packageStatus !== GetUsagesPackageStatus.NO_PACKAGE && (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <Typography variant="subtitle1">Package Information</Typography>
              {renderPackageStatusChip(packageStatus)}
            </Stack>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <InfoField
                  label="Topped up on"
                  value={formatKstToLocal(usage?.package?.toppedUpAt)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <InfoField
                  label="Activation deadline"
                  value={formatKstToLocal(
                    usage?.package?.activationDeadlineAt
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <InfoField
                  label="Activated on"
                  value={formatKstToLocal(usage?.package?.activatedAt)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <InfoField
                  label="Expires on"
                  value={formatKstToLocal(usage?.package?.expiresAt)}
                />
              </Grid>
            </Grid>
          </Paper>
        )}

      {/* USAGE INFORMATION */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="subtitle1">Usage Information</Typography>
          {renderUsageTypeChip(usageType)}
        </Stack>

        <Grid container spacing={2} mb={2}>
          <Grid item xs={12} sm={6} md={3}>
            <InfoField
              label="Total data"
              value={
                usageType === GetUsagesUsageType.UNLIMITED
                  ? 'Unlimited'
                  : formatMb(totalMb)
              }
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <InfoField label="Used data" value={formatMb(usedMb)} />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <InfoField
              label="Remaining data"
              value={
                usageType === GetUsagesUsageType.UNLIMITED || totalMb === 0
                  ? '-'
                  : formatMb(Math.max(totalMb - usedMb, 0))
              }
            />
          </Grid>
        </Grid>

        {/* Progress section */}
        <Box>
          <Stack direction="row" justifyContent="space-between" mb={1}>
            <Typography variant="body2" color="text.secondary">
              Usage progress
            </Typography>

            <Typography variant="body2" color="text.secondary">
              {usageType === GetUsagesUsageType.UNLIMITED
                ? `${formatMb(usedMb)} used (Unlimited)`
                : `${formatMb(usedMb)} / ${formatMb(
                    totalMb
                  )} (${usagePercent}%)`}
            </Typography>
          </Stack>

          {/* Only show progress bar for limited plans */}
          {usageType !== GetUsagesUsageType.UNLIMITED && (
            <Tooltip title={`${usagePercent}%`}>
              <LinearProgress
                variant={totalMb === 0 ? 'indeterminate' : 'determinate'}
                value={totalMb === 0 ? undefined : usagePercent}
              />
            </Tooltip>
          )}
        </Box>
      </Paper>

      {/* USAGE HISTORY */}
      {usageHistory.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle1" mb={2}>
            Usage History
          </Typography>

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell align="right">Used (MB)</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {usageHistory.map((row, index) => (
                <TableRow key={`${row.date ?? 'no-date'}-${index}`}>
                  <TableCell>{safeDash(row.date)}</TableCell>
                  <TableCell align="right">
                    {formatMb(row.usedMb)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Box>
  );
};
